import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { OrderContext } from '../context/OrderContext';
import { useAuth } from '../hooks/useAuth';

import {
  getExistingGuestOrdersRequest,
  getExistingOrdersRequest,
  getOrdersDeliveryDetailsRequest
} from '../api/order/order.action';

const OrderProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user, authReady, guestEmail, guestPhone } = useAuth();

  const { existingOrders, orderDeliveryDetails } = useSelector(
    (state) => state.orders,
    shallowEqual
  );

  // Local state to track loading per order
  const [loadingDelivery, setLoadingDelivery] = useState({});

  // Fetch existing orders once auth is ready — by uid for a logged-in user, or by email/phone
  // for a guest. Guests were previously skipped entirely here, which meant existingOrders stayed
  // permanently empty for every guest checkout — silently qualifying every guest order for the
  // "first order" discount in CartProvider, no matter how many times they'd ordered before.
  useEffect(() => {
    if (!authReady || !user) return;

    if (!user.isAnonymous) {
      dispatch(getExistingOrdersRequest());
    } else if (guestEmail && guestPhone) {
      dispatch(getExistingGuestOrdersRequest(guestEmail, guestPhone));
    }
    // eslint-disable-next-line
  }, [authReady, user?.uid, user?.isAnonymous, guestEmail, guestPhone]);

  const memoizedOrders = useMemo(() => {
    return existingOrders?.filter((item) => item?.paid && item?.processed) || [];
  }, [existingOrders]);

  // Fetch delivery details once per order
  useEffect(() => {
    if (!memoizedOrders || memoizedOrders.length === 0) return;

    memoizedOrders.forEach((order) => {
      const orderNumber = order.orderNumber;

      // Skip if already fetched or requested
      if (orderDeliveryDetails[orderNumber]) return;

      setLoadingDelivery((prev) => ({ ...prev, [orderNumber]: true }));

      dispatch(
        getOrdersDeliveryDetailsRequest(orderNumber, (_, error) => {
          setLoadingDelivery((prev) => ({ ...prev, [orderNumber]: false }));
        })
      );
    });
    //eslint-disable-next-line
  }, [memoizedOrders, orderDeliveryDetails]);

  return (
    <OrderContext.Provider
      value={{
        orders: memoizedOrders,
        deliveryDetails: orderDeliveryDetails,
        loadingDelivery
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

export default OrderProvider;
